import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ClinicEnvironmentService } from '../clinic-environment.service';
import { DataKlinikAdapter } from '../adapter/data-klinik.adapter.service';
import { DataKlinik, DataKlinikV2 } from '../../models/data-klinik.model';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class DataKlinikApiService implements ApiService<DataKlinik | DataKlinikV2> {
  private url = `${environment.urlV1}/data-klinik/${this.clinicEnv.clinicId || environment.klinik}`;
  private urlV2 = `${environment.urlV2}/data-klinik/${
    this.clinicEnv.clinicId || environment.klinik
  }`;

  constructor(
    private http: HttpClient,
    private adapter: DataKlinikAdapter,
    private clinicEnv: ClinicEnvironmentService
  ) {}

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  public getAll(params?: { signature: string }): Observable<DataKlinikV2[]> {
    let headers = {};
    if (params) {
      headers = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Key': params.signature,
        }),
      };
    }
    return this.http.get(`${environment.urlV2}/data-klinik/all/mobile`, headers).pipe(
      map((data: any) => {
        if (!data) {
          return [];
        }
        return data.responses.dataKlinik.map((val: any) => this.adapter.adaptV2(val));
      }),
      catchError((err) => throwError(err))
    );
  }

  public getById(idDataKlinik?: number, params?: { signature: string }): Observable<DataKlinikV2> {
    const url = idDataKlinik ? `${environment.urlV2}/data-klinik/${idDataKlinik}` : this.urlV2;
    let headers = {};
    if (params) {
      headers = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Key': params.signature,
        }),
      };
    }
    return this.http.get(url, headers).pipe(
      map((data: any) => {
        if (!data) {
          return new DataKlinik(null);
        }
        return data.responses.dataKlinik.map((val: any) =>
          this.adapter.adaptV2(val, data.responses.klinikMaker)
        )[0];
      }),
      catchError(() => of(new DataKlinik(null)))
    );
  }

  public getByUrl(url: string): Observable<DataKlinikV2> {
    const endpoint = `${environment.urlV2}/data-klinik/url/${url}/super-app`;
    return this.http.get(endpoint).pipe(
      map((data: any) => {
        if (!data) {
          return null;
        }
        return data.responses.dataKlinik.map((val: any) => this.adapter.adaptV2(val))[0];
      }),
      catchError((err) => throwError(err))
    );
  }

  public getByCustomerCode(code: string): Observable<DataKlinikV2> {
    return this.http.get(`${environment.urlV2}/data-klinik/kode-klinik/${code}`).pipe(
      map((data: any) => {
        if (!data) return null;
        return this.adapter.adaptV2(data.responses.dataKlinik);
      }),
      catchError((err) => throwError(err))
    );
  }

  public getDataHeadOffice(idGroupDataKlinik: number): Observable<DataKlinikV2> {
    return this.http.get(`${environment.urlV2}/data-klinik/head-office/${idGroupDataKlinik}`).pipe(
      map((data: any) => {
        if (!data) {
          return null;
        }

        return data.responses.dataKlinik.map((val: any) => this.adapter.adaptV2(val))[0];
      }),
      catchError((err) => throwError(err))
    );
  }

  public create(data: any, signature?: string): Observable<number> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    if (signature) {
      httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Key': signature,
        }),
      };
    }
    return this.http.post(`${environment.urlV2}/data-klinik`, data, httpOptions).pipe(
      map((data: any) => {
        return data.responses.dataKlinik.id;
      }),
      catchError((err) => throwError(err))
    );
  }

  public createNewClinic(data: any, signature?: string): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    if (signature) {
      httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Key': signature,
        }),
      };
    }
    return this.http
      .post(`${environment.urlV2}/create-new-clinic/data-klinik`, data, httpOptions)
      .pipe(catchError((err) => throwError(err)));
  }

  public update(data: any): Observable<any> {
    return this.http
      .patch(`${environment.urlV2}/data-klinik/${this.clinicEnv.clinicId}`, data, this.httpOptions)
      .pipe(catchError((err) => throwError(err)));
  }
}
